import {Table, useMediaQuery} from '@hm/fundamental'
import React, {useCallback, useEffect, useMemo, useRef} from 'react'
import {FormProvider, useForm} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {createEquipmentOptionKey} from '../../helpers'
import {createSamplingPointCountKey} from '../../helpers/samplingPointCountKey.helper'
import {
  LimsSamplingPoint,
  EquipmentOption,
  SamplingPointEquipmentType,
  SamplingPointTypeOption,
  UpmDepartment,
  RowData,
  SamplingPointsCountsMap,
  SortDirection,
  SortByColumn
} from '../../types'

import {useSamplingPointsSettingsTableConfig} from './hooks/useSamplingPointsSettingsTableConfig'
import {SamplingPointsMobileSorting} from './SamplingPointsMobileSorting'
import {
  SamplingPointsTableFormRow,
  SamplingPointsTableFormValues
} from './SamplingPointsTableForm.types'

const createOptionalEquipmentOptionKey = (equipmentOption: EquipmentOption | null | undefined) =>
  equipmentOption ? createEquipmentOptionKey(equipmentOption) : null

const getInitialEquipmentOption = ({
  typeOption,
  limsSamplingPoint,
  assetOptions,
  grindingCircuitOptions,
  storageOptions
}: {
  typeOption: SamplingPointTypeOption | null
  limsSamplingPoint: LimsSamplingPoint
  assetOptions: EquipmentOption[]
  grindingCircuitOptions: EquipmentOption[]
  storageOptions: EquipmentOption[]
}) => {
  if (!typeOption) {
    return null
  }

  if (typeOption.type === SamplingPointEquipmentType.asset) {
    return (
      [...assetOptions, ...grindingCircuitOptions].find((equipmentOption) =>
        [
          limsSamplingPoint.samplingPoint?.assetId,
          limsSamplingPoint.samplingPoint?.grindingCircuitId
        ].includes(equipmentOption.id)
      ) ?? null
    )
  }

  if (typeOption.type === SamplingPointEquipmentType.storage) {
    return (
      storageOptions.find(
        (storageOption) => storageOption.id === limsSamplingPoint.samplingPoint?.storageId
      ) ?? null
    )
  }

  return null
}

const mapFormRowToRowData = (
  formRow: SamplingPointsTableFormRow,
  samplingPointTypeOptionsMap: Map<number, SamplingPointTypeOption>,
  equipmentOptionsMap: Map<string, EquipmentOption>,
  upmDepartmentOptionsMap: Map<string, UpmDepartment>
): RowData => ({
  typeOption:
    formRow.typeOptionId !== null && formRow.typeOptionId !== undefined
      ? (samplingPointTypeOptionsMap.get(formRow.typeOptionId) ?? null)
      : null,
  equipmentOption:
    formRow.equipmentOptionKey !== null && formRow.equipmentOptionKey !== undefined
      ? (equipmentOptionsMap.get(formRow.equipmentOptionKey) ?? null)
      : null,
  upmDepartmentOption:
    formRow.upmDepartmentOptionId !== null && formRow.upmDepartmentOptionId !== undefined
      ? (upmDepartmentOptionsMap.get(formRow.upmDepartmentOptionId) ?? null)
      : null
})

export interface SamplingPointsSettingsTableProps {
  limsSamplingPoints: LimsSamplingPoint[]
  samplingPointTypeOptions: SamplingPointTypeOption[]
  assetOptions: EquipmentOption[]
  upmDepartmentOptions: UpmDepartment[]
  grindingCircuitOptions: EquipmentOption[]
  storageOptions: EquipmentOption[]
  samplingPointsCountsMap: SamplingPointsCountsMap
  isSaving: boolean
  getRowData: (limsSamplingPointId: number) => RowData | undefined
  getOriginalRowData: (limsSamplingPointId: number) => RowData | undefined
  updateRowData: (limsSamplingPointId: number, data: RowData, initialData?: RowData) => void
  pinnedSamplingPointIds: Set<number>
  onTogglePinnedSamplingPoint: (limsSamplingPointId: number) => void
  lessUsedSamplingPointIds: Set<number>
  onToggleLessUsedSamplingPoint: (limsSamplingPointId: number) => void
  showMobileSorting?: boolean
}

export const SamplingPointsSettingsTable: React.FC<SamplingPointsSettingsTableProps> = ({
  limsSamplingPoints,
  samplingPointTypeOptions,
  assetOptions,
  upmDepartmentOptions,
  grindingCircuitOptions,
  storageOptions,
  samplingPointsCountsMap,
  isSaving,
  getRowData,
  getOriginalRowData,
  updateRowData,
  pinnedSamplingPointIds,
  onTogglePinnedSamplingPoint,
  lessUsedSamplingPointIds,
  onToggleLessUsedSamplingPoint,
  showMobileSorting = true
}) => {
  const {t} = useTranslation()
  const isMobile = useMediaQuery({breakpoint: 'md', upTo: true})
  const [sortBy, setSortBy] = React.useState<SortByColumn>(SortByColumn.CODE)
  const [sortDirection, setSortDirection] = React.useState<SortDirection>(SortDirection.ASCENDING)

  const getSamplingPointsCount = useCallback(
    (limsSamplingPoint: LimsSamplingPoint) =>
      samplingPointsCountsMap.get(
        createSamplingPointCountKey(limsSamplingPoint.code, limsSamplingPoint.description)
      ) ?? 0,
    [samplingPointsCountsMap]
  )
  const orderedLimsSamplingPoints = useMemo(() => {
    const sortedLimsSamplingPoints = [...limsSamplingPoints].sort((current, next) => {
      if (sortBy === SortByColumn.COUNTS) {
        const countsDifference = getSamplingPointsCount(current) - getSamplingPointsCount(next)

        if (countsDifference !== 0) {
          return sortDirection === SortDirection.ASCENDING ? countsDifference : -countsDifference
        }
      }

      const codeDifference = current.code.localeCompare(next.code)

      if (codeDifference !== 0) {
        return sortDirection === SortDirection.ASCENDING ? codeDifference : -codeDifference
      }

      const currentDescription = current.description ?? ''
      const nextDescription = next.description ?? ''
      const descriptionDifference = currentDescription.localeCompare(nextDescription)

      return sortDirection === SortDirection.ASCENDING
        ? descriptionDifference
        : -descriptionDifference
    })

    return sortedLimsSamplingPoints
  }, [getSamplingPointsCount, limsSamplingPoints, sortBy, sortDirection])

  const samplingPointTypeOptionsMap = useMemo(
    () => new Map(samplingPointTypeOptions.map((option) => [option.id, option])),
    [samplingPointTypeOptions]
  )

  const equipmentOptionsMap = useMemo(
    () =>
      new Map(
        [...assetOptions, ...grindingCircuitOptions, ...storageOptions].map((option) => [
          createEquipmentOptionKey(option),
          option
        ])
      ),
    [assetOptions, grindingCircuitOptions, storageOptions]
  )

  const upmDepartmentOptionsMap = useMemo(
    () => new Map(upmDepartmentOptions.map((department) => [department.id, department])),
    [upmDepartmentOptions]
  )

  const defaultRowsById = useMemo(() => {
    const rowsById: Record<string, SamplingPointsTableFormRow> = {}

    limsSamplingPoints.forEach((limsSamplingPoint) => {
      const editedRowData = getRowData(limsSamplingPoint.id)

      const initialTypeOption =
        editedRowData?.typeOption ??
        samplingPointTypeOptions.find(
          (option) => option.label === limsSamplingPoint.samplingPoint?.type
        ) ??
        null

      const initialEquipmentOption =
        editedRowData?.equipmentOption ??
        getInitialEquipmentOption({
          typeOption: initialTypeOption,
          limsSamplingPoint,
          assetOptions,
          grindingCircuitOptions,
          storageOptions
        })

      const initialUpmDepartmentOption =
        editedRowData?.upmDepartmentOption ??
        upmDepartmentOptions.find(
          (department) => department.id === limsSamplingPoint.samplingPoint?.upmDepartmentId
        ) ??
        null

      rowsById[String(limsSamplingPoint.id)] = {
        typeOptionId: initialTypeOption?.id ?? null,
        equipmentOptionKey: createOptionalEquipmentOptionKey(initialEquipmentOption),
        upmDepartmentOptionId: initialUpmDepartmentOption?.id ?? null
      }
    })

    return rowsById
  }, [
    limsSamplingPoints,
    getRowData,
    samplingPointTypeOptions,
    assetOptions,
    grindingCircuitOptions,
    storageOptions,
    upmDepartmentOptions
  ])

  const methods = useForm<SamplingPointsTableFormValues>({
    shouldFocusError: false,
    defaultValues: {
      rowsById: defaultRowsById
    }
  })

  const isInitializedRef = useRef(false)

  useEffect(() => {
    if (isInitializedRef.current) {
      return
    }

    limsSamplingPoints.forEach((limsSamplingPoint) => {
      const rowFormData = methods.getValues(`rowsById.${limsSamplingPoint.id}`)

      if (!rowFormData || getOriginalRowData(limsSamplingPoint.id)) {
        return
      }

      const initialRowData = mapFormRowToRowData(
        rowFormData,
        samplingPointTypeOptionsMap,
        equipmentOptionsMap,
        upmDepartmentOptionsMap
      )

      updateRowData(limsSamplingPoint.id, initialRowData, initialRowData)
    })

    isInitializedRef.current = true
  }, [
    limsSamplingPoints,
    methods,
    getOriginalRowData,
    updateRowData,
    samplingPointTypeOptionsMap,
    equipmentOptionsMap,
    upmDepartmentOptionsMap
  ])

  useEffect(() => {
    const subscription = methods.watch((_, {name}) => {
      if (!name || !name.startsWith('rowsById.')) {
        return
      }

      const [, limsSamplingPointId] = name.split('.')
      const rowFormData = methods.getValues(`rowsById.${limsSamplingPointId}`)

      if (!rowFormData) {
        return
      }

      const newRowData = mapFormRowToRowData(
        rowFormData,
        samplingPointTypeOptionsMap,
        equipmentOptionsMap,
        upmDepartmentOptionsMap
      )

      updateRowData(Number(limsSamplingPointId), newRowData)
    })

    return () => subscription.unsubscribe()
  }, [
    methods,
    updateRowData,
    samplingPointTypeOptionsMap,
    equipmentOptionsMap,
    upmDepartmentOptionsMap
  ])

  const toggleSorting = (column: SortByColumn) => {
    if (sortBy === column) {
      setSortDirection((currentSortDirection) =>
        currentSortDirection === SortDirection.ASCENDING
          ? SortDirection.DESCENDING
          : SortDirection.ASCENDING
      )

      return
    }

    setSortBy(column)
    setSortDirection(SortDirection.ASCENDING)
  }

  const tableConfig = useSamplingPointsSettingsTableConfig({
    isMobile,
    sortBy,
    sortDirection,
    onToggleSorting: toggleSorting,
    getSamplingPointsCount,
    t,
    samplingPointTypeOptions,
    assetOptions,
    grindingCircuitOptions,
    storageOptions,
    upmDepartmentOptions,
    isSaving,
    pinnedSamplingPointIds,
    onTogglePinnedSamplingPoint,
    lessUsedSamplingPointIds,
    onToggleLessUsedSamplingPoint
  })

  return (
    <FormProvider {...methods}>
      <div className="w-full max-w-full min-h-0 overflow-x-auto overflow-y-visible">
        {showMobileSorting && isMobile && (
          <SamplingPointsMobileSorting
            sortByLabel={t('samplingPointsSettings.sortBy')}
            samplingPointCodeLabel={t('samplingPointsSettings.samplingPointCode')}
            samplingPointCountsLabel={t('samplingPointsSettings.samplingPointCounts')}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onToggleSorting={toggleSorting}
          />
        )}

        <Table
          className="w-full max-w-full table-fixed [&_thead]:h-auto [&_thead_tr]:h-auto [&_thead_td]:h-auto [&_thead_td]:align-middle"
          config={tableConfig}
          data={orderedLimsSamplingPoints}
          getRowKey={(row) => `sampling-point-row-${row.id}`}
          breakpoint="md"
          data-cy="sampling_points_table"
        />
      </div>
    </FormProvider>
  )
}
